import {
    Box, Drawer, Grid, IconButton, MenuItem, Select, Typography, Button, Divider
} from "@mui/material"
import { FC, useEffect, useState } from "react"
import CloseIcon from '@mui/icons-material/Close'
import SaveIcon from '@mui/icons-material/Save'
import { company } from "../../../../../types/slices/companyType"
import { useCompanyStore } from "../../../../../hooks/useCompanyStore"

interface SettingBillingDrawerProps {
    open: boolean
    onClose: () => void
    companySelected: company
}

const options = [
    { id: '1', label: 'Netos' },
    { id: '2', label: 'Brutos' },
]

type SettingBilling = {
    id: number
    documenttype: '1' | '2' | string
    calculationtype: '1' | '2' | string
    idcompany: number
    status: '0' | '1' | '2'
}

export const SettingBillingDrawer: FC<SettingBillingDrawerProps> = ({
    open,
    onClose,
    companySelected
}) => {
    const { getSettingBilling, updateSettingBilling } = useCompanyStore()

    const [receiptSetting, setReceiptSetting] = useState<SettingBilling | null>(null)
    const [invoiceSetting, setInvoiceSetting] = useState<SettingBilling | null>(null)

    const [originalReceipt, setOriginalReceipt] = useState<string | null>(null)
    const [originalInvoice, setOriginalInvoice] = useState<string | null>(null)

    const [isModified, setIsModified] = useState(false)

    useEffect(() => {
        if (open && companySelected?.id) {
            getDataAPI()
        }
    }, [open, companySelected])

    const getDataAPI = async (): Promise<void> => {
        const resp = await getSettingBilling(companySelected.id)
        if (resp.length > 0) {
            const receipt = resp.find(row => row.documenttype === '1') || null
            const invoice = resp.find(row => row.documenttype === '2') || null

            setReceiptSetting(receipt)
            setInvoiceSetting(invoice)

            setOriginalReceipt(receipt?.calculationtype || null)
            setOriginalInvoice(invoice?.calculationtype || null)
        }
    }

    const handleChangeReceipt = (value: string): void => {
        if (receiptSetting) {
            setReceiptSetting({ ...receiptSetting, calculationtype: value })
        }
    }

    const handleChangeInvoice = (value: string): void => {
        if (invoiceSetting) {
            setInvoiceSetting({ ...invoiceSetting, calculationtype: value })
        }
    }

    useEffect(() => {
        if (
            receiptSetting?.calculationtype !== originalReceipt ||
            invoiceSetting?.calculationtype !== originalInvoice
        ) {
            setIsModified(true)
        } else {
            setIsModified(false)
        }
    }, [receiptSetting, invoiceSetting, originalReceipt, originalInvoice])

    const handleSave = async (): Promise<void> => {
        const resp = await updateSettingBilling({
            settings: [receiptSetting, invoiceSetting]
        })

        if (resp === true) {
            setOriginalReceipt(receiptSetting?.calculationtype || null)
            setOriginalInvoice(invoiceSetting?.calculationtype || null)
            setIsModified(false)
        }

    }

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: 480 },
                    borderRadius: '12px 0 0 12px',
                    backgroundColor: '#fafafa',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                px={2}
                py={1.5}
                sx={{
                    backgroundColor: '#f5f7f9',
                    borderBottom: '1px solid #e0e0e0'
                }}
            >
                <Box>
                    <Typography variant="h6" fontWeight={700} color="primary">
                        Configuración de facturación
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary' }}
                    >
                        {companySelected?.business_name || 'Compañía'}
                    </Typography>
                </Box>

                <IconButton
                    onClick={onClose}
                    sx={{
                        backgroundColor: 'rgba(0,0,0,0.04)',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' },
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Boleta
                </Typography>
                <Select
                    fullWidth
                    size="small"
                    value={receiptSetting?.calculationtype || ''}
                    onChange={(e) => handleChangeReceipt(e.target.value)}
                >
                    {options.map(opt => (
                        <MenuItem key={opt.id} value={opt.id}>
                            {opt.label}
                        </MenuItem>
                    ))}
                </Select>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Factura
                </Typography>
                <Select
                    fullWidth
                    size="small"
                    value={invoiceSetting?.calculationtype || ''}
                    onChange={(e) => handleChangeInvoice(e.target.value)}
                >
                    {options.map(opt => (
                        <MenuItem key={opt.id} value={opt.id}>
                            {opt.label}
                        </MenuItem>
                    ))}
                </Select>
            </Box>

            {isModified && (
                <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        color="primary"
                        fullWidth
                        onClick={handleSave}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 2
                        }}
                    >
                        Guardar cambios
                    </Button>
                </Box>
            )}
        </Drawer>
    )
}
